import type { UserWithAggInfo } from "@/api/custom_types";
import {
	type ExecutionPlanVersion,
	ExecutionPlanVersionChoice,
} from "@/components/base/ExecutionPlanVersionChoice";
import {
	PasswordField,
	generatePassword,
} from "@/components/base/PasswordField";
import { useApiError } from "@/hooks";
import { useFacets } from "@/hooks/useFacetFilters";
import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";
import {
	ADMIN_USERS_FACET_SOURCE,
	useCreateAdminUser,
	useGetAdminUsers,
	useGetStSetting,
	useUpdateAdminUser,
} from "@/services";
import { cleanQuery } from "@/utils/queryClient";
import { formatNumber } from "@/utils/tools";
import { zodResolver } from "@hookform/resolvers/zod";
import {
	ActionItem,
	ActionMenu,
	Button,
	Checkbox,
	DataTable,
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogScrollableArea,
	DialogTitle,
	Form,
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
	Icon,
	Input,
	Label,
	Switch,
	Typography,
	useServerTableState,
} from "@st-marketplace/ui";
import type { ColumnDef } from "@tanstack/react-table";
import { useEffect, useId, useMemo, useState } from "react";
import { type Control, Controller, type Path, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { z } from "zod";
import ResetPasswordDialog from "./ResetPasswordDialog";
import ResolvedCommissionCell from "./ResolvedCommissionCell";
import {
	type AdminUsersFilters,
	STAdminUsersFilterForm,
	emptyAdminUsersFilters,
} from "./STAdminUsersFilterForm";

type CreateUserFormData = {
	email: string;
	password: string;
	proxy_owner_type: "PERSONAL" | "SYSTEM";
	is_buyer: boolean;
	max_debt: number;
	use_inherited_price: boolean;
	local_price: number | null;
	is_allowed_to_order: boolean;
	execution_plan_version: ExecutionPlanVersion;
};

type EditUserFormData = {
	email: string;
	use_global_commission: boolean;
	sys_commission_rate: number | null;
	sys_current_debt: number;
	is_verified: boolean;
	execution_plan_version: ExecutionPlanVersion;
};

type CreatedCredentials = Pick<CreateUserFormData, "email" | "password">;

const getCreateUserDefaults = (): CreateUserFormData => ({
	email: "",
	password: generatePassword({ allowSymbols: false }),
	proxy_owner_type: "PERSONAL",
	is_buyer: true,
	max_debt: 0,
	use_inherited_price: true,
	local_price: null,
	is_allowed_to_order: true,
	execution_plan_version: "v2",
});

/** The seller's planner: which version executes; the other runs in shadow. */
function ExecutionPlanVersionField<
	T extends { execution_plan_version: ExecutionPlanVersion },
>({ control }: { control: Control<T> }) {
	const { t } = useTranslation();
	const labelId = useId();
	return (
		<Controller
			name={"execution_plan_version" as Path<T>}
			control={control}
			render={({ field }) => (
				<FormItem>
					<FormLabel id={labelId}>{t("executionPlanVersion.title")}</FormLabel>
					<ExecutionPlanVersionChoice
						aria-labelledby={labelId}
						value={field.value}
						onChange={field.onChange}
					/>
				</FormItem>
			)}
		/>
	);
}

interface EditUserDialogProps {
	open: boolean;
	user: UserWithAggInfo | null;
	loading: boolean;
	onClose: () => void;
	onSubmit: (data: EditUserFormData) => void;
}

const EditUserDialog: React.FC<EditUserDialogProps> = ({
	open,
	user,
	loading,
	onClose,
	onSubmit,
}) => {
	const { t } = useTranslation();

	const { data: stSetting } = useGetStSetting();
	const globalCommissionRate = stSetting?.default_commission_rate ?? null;

	const sysCommissionRate =
		user?.sys_commission_rate == null ? null : Number(user.sys_commission_rate);
	const sysCurrentDebt = Number(user?.sys_current_debt ?? "0");
	const useGlobalCommissionInitial = user?.sys_commission_rate == null;

	const editSchema = useMemo(
		() =>
			z
				.object({
					email: z.string().email(t("validation.email")),
					use_global_commission: z.boolean(),
					sys_commission_rate: z.preprocess(
						(v) => (v === "" || v == null ? null : Number(v)),
						z.number().nullable(),
					),
					sys_current_debt: z.coerce
						.number({ invalid_type_error: t("validation.required") })
						.refine((value) => Number.isFinite(value), {
							message: t("validation.required"),
						}),
					is_verified: z.coerce.boolean(),
					execution_plan_version: z.enum(["v1", "v2"]),
				})
				.superRefine((data, context) => {
					// In global mode the custom rate is unused, so skip its
					// validation entirely instead of blocking the save.
					if (data.use_global_commission) return;
					if (
						data.sys_commission_rate === null ||
						!Number.isFinite(data.sys_commission_rate)
					) {
						context.addIssue({
							code: z.ZodIssueCode.custom,
							path: ["sys_commission_rate"],
							message: t("validation.required"),
						});
					} else if (data.sys_commission_rate < 0) {
						context.addIssue({
							code: z.ZodIssueCode.too_small,
							minimum: 0,
							type: "number",
							inclusive: true,
							path: ["sys_commission_rate"],
						});
					}
				}),
		[t],
	);

	const form = useForm<EditUserFormData>({
		resolver: zodResolver(editSchema),
		defaultValues: {
			email: user?.email ?? "",
			use_global_commission: useGlobalCommissionInitial,
			sys_commission_rate: sysCommissionRate,
			sys_current_debt: sysCurrentDebt,
			is_verified: user?.is_verified ?? false,
			execution_plan_version: user?.execution_plan_version ?? "v2",
		},
	});

	useEffect(() => {
		if (!user) return;
		form.reset({
			email: user.email,
			use_global_commission: useGlobalCommissionInitial,
			sys_commission_rate: sysCommissionRate,
			sys_current_debt: sysCurrentDebt,
			is_verified: user.is_verified,
			execution_plan_version: user.execution_plan_version ?? "v2",
		});
	}, [
		form,
		user,
		useGlobalCommissionInitial,
		sysCommissionRate,
		sysCurrentDebt,
	]);

	const useGlobalCommissionValue = form.watch("use_global_commission");

	return (
		<Dialog open={open} onOpenChange={(o) => !o && onClose()}>
			<DialogContent className="w-full max-w-120">
				<DialogHeader>
					<DialogTitle>{t("admin.users.editTitle")}</DialogTitle>
				</DialogHeader>
				<DialogScrollableArea>
					<Form {...form}>
						<form
							noValidate
							onSubmit={form.handleSubmit(onSubmit)}
							className="flex flex-col gap-4"
							id="admin-user-edit-form"
						>
							<FormField
								control={form.control}
								name="email"
								render={({ field }) => (
									<FormItem>
										<FormLabel required>
											{t("admin.users.table.email")}
										</FormLabel>
										<FormControl>
											<Input
												{...field}
												ltr
												placeholder={t("admin.users.table.email")}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<div className="grid grid-cols-1 gap-4">
								<div className="flex flex-col gap-2">
									<Controller
										name="use_global_commission"
										control={form.control}
										render={({ field }) => (
											<FormItem className="flex flex-row items-center gap-2">
												<Checkbox
													checked={Boolean(field.value)}
													onCheckedChange={(checked) =>
														field.onChange(checked === true)
													}
												/>
												<Label>{t("admin.users.commissionUseGlobal")}</Label>
											</FormItem>
										)}
									/>
									<FormField
										control={form.control}
										name="sys_commission_rate"
										render={({ field }) => (
											<FormItem>
												<FormLabel required={!useGlobalCommissionValue}>
													{t("admin.users.table.commission")}
												</FormLabel>
												<FormControl>
													<Input
														type="number"
														min={0}
														placeholder={t("admin.users.table.commission")}
														value={field.value ?? ""}
														onChange={field.onChange}
														disabled={useGlobalCommissionValue}
													/>
												</FormControl>
												{useGlobalCommissionValue &&
													globalCommissionRate != null && (
														<FormDescription>
															{t("admin.users.commissionGlobalInUse", {
																value: formatNumber(globalCommissionRate),
															})}
														</FormDescription>
													)}
												<FormMessage />
											</FormItem>
										)}
									/>
								</div>
								<FormField
									control={form.control}
									name="sys_current_debt"
									render={({ field }) => (
										<FormItem>
											<FormLabel required>
												{t("admin.users.table.balance")}
											</FormLabel>
											<FormControl>
												<Input
													type="number"
													placeholder={t("admin.users.table.balance")}
													value={field.value ?? ""}
													onChange={field.onChange}
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>

							<Controller
								name="is_verified"
								control={form.control}
								render={({ field }) => (
									<FormItem className="flex flex-row items-center gap-2">
										<Switch
											size="sm"
											checked={Boolean(field.value)}
											onCheckedChange={field.onChange}
										/>
										<Label>{t("admin.users.table.isVerified")}</Label>
									</FormItem>
								)}
							/>

							<ExecutionPlanVersionField control={form.control} />
						</form>
					</Form>
				</DialogScrollableArea>
				<DialogFooter
					buttons={{
						secondary: {
							label: t("button.cancel"),
							props: { type: "button", onClick: onClose },
						},
						primary: {
							label: t("button.submit"),
							props: {
								type: "submit",
								form: "admin-user-edit-form",
								loading,
								disabled: !user,
							},
						},
					}}
				/>
			</DialogContent>
		</Dialog>
	);
};

interface AdminUserRowActionProps {
	user: UserWithAggInfo;
	onEdit: (user: UserWithAggInfo) => void;
	onResetPassword: (user: UserWithAggInfo) => void;
}

const AdminUserRowAction: React.FC<AdminUserRowActionProps> = ({
	user,
	onEdit,
	onResetPassword,
}) => {
	const { t } = useTranslation();
	const [menuActionOpen, setMenuActionOpen] = useState<boolean>(false);

	return (
		// biome-ignore lint/a11y/useKeyWithClickEvents: stopPropagation-only container; inner menu remains keyboard-accessible
		<div onClick={(event) => event.stopPropagation()}>
			<ActionMenu open={menuActionOpen} onOpenChange={setMenuActionOpen}>
				<ActionItem
					leadingIconName="edit-line"
					onClick={() => {
						setMenuActionOpen(false);
						onEdit(user);
					}}
				>
					{t("action.edit")}
				</ActionItem>
				<ActionItem
					leadingIconName="key-line"
					onClick={() => {
						setMenuActionOpen(false);
						onResetPassword(user);
					}}
				>
					{t("admin.users.resetPasswordAction")}
				</ActionItem>
			</ActionMenu>
		</div>
	);
};

const STAdminUsers = () => {
	const { t } = useTranslation();
	const { getErrorInterface } = useApiError();
	const createUserMutation = useCreateAdminUser();
	const updateUserMutation = useUpdateAdminUser();

	const [editingUser, setEditingUser] = useState<UserWithAggInfo | null>(null);
	const [resettingUser, setResettingUser] = useState<UserWithAggInfo | null>(
		null,
	);
	const [isCreateOpen, setIsCreateOpen] = useState(false);
	const [createdCredentials, setCreatedCredentials] =
		useState<CreatedCredentials | null>(null);
	const [copiedCredentials, setCopiedCredentials] = useState(false);

	const createSchema = useMemo(
		() =>
			z
				.object({
					email: z.string().email(t("validation.email")),
					password: z.string().min(
						6,
						t("validation.atLeast", {
							field: t("auth.password"),
							num: 6,
						}),
					),
					proxy_owner_type: z.enum(["PERSONAL", "SYSTEM"]),
					is_buyer: z.boolean(),
					max_debt: z.coerce.number().min(0),
					use_inherited_price: z.boolean(),
					local_price: z.preprocess(
						(v) => (v === "" || v == null ? null : Number(v)),
						z.number().min(0).nullable(),
					),
					is_allowed_to_order: z.boolean(),
					execution_plan_version: z.enum(["v1", "v2"]),
				})
				.superRefine((data, context) => {
					if (
						data.is_buyer &&
						!data.use_inherited_price &&
						data.local_price === null
					) {
						context.addIssue({
							code: z.ZodIssueCode.custom,
							path: ["local_price"],
							message: t("validation.required"),
						});
					}
				}),
		[t],
	);

	const createForm = useForm<CreateUserFormData>({
		resolver: zodResolver(createSchema),
		defaultValues: getCreateUserDefaults(),
	});

	const isBuyer = createForm.watch("is_buyer");
	const useInheritedPrice = createForm.watch("use_inherited_price");

	const server = useServerTableState();
	const [filters, setFilters] = useState<AdminUsersFilters>(
		emptyAdminUsersFilters(),
	);

	const filterQuery = useMemo(
		() =>
			cleanQuery({
				search_field: filters.search.trim() || undefined,
				proxy_owner_types: filters.proxyOwnerTypes.length
					? (filters.proxyOwnerTypes as ("PERSONAL" | "SYSTEM")[])
					: undefined,
				local_commission:
					filters.localCommission === ""
						? undefined
						: filters.localCommission === "custom",
			}),
		[filters],
	);

	const listQuery = {
		...filterQuery,
		page: server.pageIndex + 1,
		page_size: server.pageSize,
	};

	const facetCounts = useFacets({
		source: ADMIN_USERS_FACET_SOURCE,
		filters: filterQuery,
		ownParams: {
			proxy_owner_types: "proxy_owner_types",
			local_commission: "local_commission",
		},
	});

	const handleSearch = (values: AdminUsersFilters) => {
		setFilters(values);
		server.onPageChange(0);
	};

	const {
		data: users,
		isLoading,
		isFetching,
		error,
		refetch,
	} = useGetAdminUsers({ query: listQuery });

	const rows = users?.results ?? [];
	const rowCount = users?.count ?? rows.length;

	const handleOpenCreate = () => {
		createForm.reset(getCreateUserDefaults());
		setIsCreateOpen(true);
	};

	const handleCloseCreate = () => {
		setIsCreateOpen(false);
		setCreatedCredentials(null);
		setCopiedCredentials(false);
		createForm.reset(getCreateUserDefaults());
	};

	const onboardingMessage = createdCredentials
		? t("admin.users.onboardingMessage", {
				dashboardUrl: `${window.location.origin}/dashboard`,
				email: createdCredentials.email,
				password: createdCredentials.password,
			})
		: "";

	const handleCopyCredentials = async () => {
		try {
			await navigator.clipboard.writeText(onboardingMessage);
			setCopiedCredentials(true);
		} catch {
			setCopiedCredentials(false);
			toast.error(t("error.unexpected"));
		}
	};

	const handleCreateSubmit = createForm.handleSubmit((values) => {
		createUserMutation.mutate(
			{
				body: {
					email: values.email.trim(),
					password: values.password,
					proxy_owner_type: values.proxy_owner_type,
					execution_plan_version: values.execution_plan_version,
					buyer_relation: values.is_buyer
						? {
								max_debt: values.max_debt,
								local_price: values.local_price,
								is_allowed_to_order: values.is_allowed_to_order,
							}
						: null,
				},
			},
			{
				onSuccess: () => {
					setCreatedCredentials({
						email: values.email.trim(),
						password: values.password,
					});
					setCopiedCredentials(false);
					createForm.reset();
					server.onPageChange(0);
				},
			},
		);
	});

	const handleEditSubmit = (values: EditUserFormData) => {
		if (!editingUser) return;
		updateUserMutation.mutate(
			{
				path: { user_id: editingUser.id },
				body: {
					email: values.email.trim(),
					sys_commission_rate: values.use_global_commission
						? null
						: values.sys_commission_rate,
					sys_current_debt: values.sys_current_debt,
					is_verified: values.is_verified,
					execution_plan_version: values.execution_plan_version,
				},
			},
			{
				onSuccess: () => {
					setEditingUser(null);
				},
			},
		);
	};

	const columns = useMemo<ColumnDef<UserWithAggInfo>[]>(
		() => [
			{
				id: "username",
				header: t("admin.users.table.username"),
				enableSorting: false,
				cell: ({ row }) => row.original.username,
			},
			{
				id: "display_name",
				header: t("admin.users.table.name"),
				enableSorting: false,
				cell: ({ row }) => row.original.display_name || "-",
			},
			{
				id: "sys_commission_rate",
				header: t("admin.users.table.commission"),
				enableSorting: false,
				cell: ({ row }) => (
					<ResolvedCommissionCell
						resolved={row.original.resolved_sys_commission_rate}
					/>
				),
			},
			{
				id: "sys_current_debt",
				header: t("admin.users.table.balance"),
				enableSorting: false,
				cell: ({ row }) =>
					row.original.sys_current_debt == null
						? "-"
						: formatNumber(row.original.sys_current_debt),
			},
			{
				id: "ordered_last_48h",
				header: t("admin.users.table.orderedLast48h"),
				enableSorting: false,
				cell: ({ row }) =>
					row.original.ordered_last_48h == null
						? "-"
						: formatNumber(row.original.ordered_last_48h),
			},
			{
				id: "total_debt_to_users",
				header: t("admin.users.table.totalDebtToUsers"),
				enableSorting: false,
				cell: ({ row }) =>
					row.original.total_debt_to_users == null
						? "-"
						: formatNumber(row.original.total_debt_to_users),
			},
			{
				id: "is_verified",
				header: t("admin.users.table.isVerified"),
				enableSorting: false,
				cell: ({ row }) => {
					const ok = Boolean(row.original.is_verified);

					return (
						<span className="inline-flex items-center">
							{ok ? (
								<Icon
									name="checkbox-circle-line"
									size="sm"
									className="text-success-text"
								/>
							) : (
								<Icon
									name="close-circle-line"
									size="sm"
									className="text-danger-text"
								/>
							)}
						</span>
					);
				},
			},
			{
				id: "id",
				header: t("admin.users.table.id"),
				enableSorting: false,
				cell: ({ row }) => row.original.id,
			},
			{
				id: "actions",
				header: "",
				enableSorting: false,
				cell: ({ row }) => (
					<AdminUserRowAction
						user={row.original}
						onEdit={setEditingUser}
						onResetPassword={setResettingUser}
					/>
				),
			},
		],
		[t],
	);

	return (
		<div className="flex flex-col gap-6">
			<div className="flex items-center justify-between gap-2">
				<div>
					<Typography variant="title-md" as="h1">
						{t("admin.users.title")}
					</Typography>
					<Typography variant="body-sm" className="text-secondary-text-100">
						{t("admin.users.description")}
					</Typography>
				</div>

				<Button
					type="button"
					color="primary"
					className="normal-case"
					startIcon={<Icon name="user-add-line" />}
					onClick={handleOpenCreate}
				>
					{t("admin.users.createButton")}
				</Button>
			</div>

			<Dialog
				open={isCreateOpen}
				onOpenChange={(open) =>
					open ? setIsCreateOpen(true) : handleCloseCreate()
				}
			>
				<DialogContent className="w-full max-w-120">
					<DialogHeader>
						<DialogTitle>
							{t(
								createdCredentials
									? "admin.users.onboardingTitle"
									: "admin.users.createTitle",
							)}
						</DialogTitle>
					</DialogHeader>
					<DialogScrollableArea>
						{createdCredentials ? (
							<div className="flex flex-col gap-5">
								<div className="flex items-start gap-3 rounded-xl border border-success-text/30 bg-success-text/5 p-4">
									<Icon
										name="checkbox-circle-line"
										size="lg"
										className="shrink-0 text-success-text"
									/>
									<Typography variant="body-sm">
										{t("admin.users.onboardingDescription")}
									</Typography>
								</div>

								<textarea
									readOnly
									value={onboardingMessage}
									rows={10}
									aria-label={t("admin.users.onboardingMessageLabel")}
									className="w-full resize-none rounded-xl border border-secondary-border bg-background-100 p-4 font-mono text-sm leading-6 outline-none focus:border-primary-border"
								/>

								<Button
									type="button"
									color="primary"
									className="w-full"
									startIcon={
										<Icon
											name={copiedCredentials ? "check-line" : "file-copy-line"}
										/>
									}
									onClick={handleCopyCredentials}
								>
									{t(
										copiedCredentials
											? "admin.users.copiedOnboardingMessage"
											: "admin.users.copyOnboardingMessage",
									)}
								</Button>
							</div>
						) : (
							<Form {...createForm}>
								<form
									noValidate
									autoComplete="off"
									onSubmit={handleCreateSubmit}
									className="flex flex-col gap-4"
									id="admin-user-create-form"
								>
									<FormField
										control={createForm.control}
										name="email"
										render={({ field }) => (
											<FormItem>
												<FormLabel required>{t("auth.email")}</FormLabel>
												<FormControl>
													<Input
														{...field}
														ltr
														autoComplete="off"
														placeholder={t("auth.email")}
													/>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>

									<PasswordField
										name="password"
										label={t("auth.password")}
										control={createForm.control}
										required
										autoGenerate
										autoComplete="new-password"
										allowSymbols={false}
									/>

									<Controller
										name="proxy_owner_type"
										control={createForm.control}
										render={({ field }) => (
											<FormItem>
												<FormLabel>{t("admin.users.proxyType")}</FormLabel>
												<div
													role="radiogroup"
													className="grid grid-cols-2 gap-2"
												>
													{(
														[
															["PERSONAL", "proxyPersonal"],
															["SYSTEM", "proxyShared"],
														] as const
													).map(([value, label]) => {
														const selected = field.value === value;
														return (
															<button
																key={value}
																type="button"
																role="radio"
																aria-checked={selected}
																onClick={() => field.onChange(value)}
																className={cn(
																	"flex h-12 items-center justify-between rounded-xl border px-4 font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-border",
																	selected
																		? "border-primary-border bg-primary-fill/10 text-primary-text"
																		: "border-secondary-border bg-background-100 text-secondary-text-100 hover:bg-background-300",
																)}
															>
																{t(`admin.users.${label}`)}
																<span
																	className={cn(
																		"flex size-5 items-center justify-center rounded-full border",
																		selected
																			? "border-primary-fill bg-primary-fill text-white"
																			: "border-secondary-border",
																	)}
																>
																	{selected && (
																		<Icon name="check-line" size="xs" />
																	)}
																</span>
															</button>
														);
													})}
												</div>
											</FormItem>
										)}
									/>

									<ExecutionPlanVersionField control={createForm.control} />

									<Controller
										name="is_buyer"
										control={createForm.control}
										render={({ field }) => (
											<FormItem>
												<Checkbox
													label={t("admin.users.isBuyer")}
													checked={field.value}
													onCheckedChange={field.onChange}
												/>
											</FormItem>
										)}
									/>

									{isBuyer && (
										<div className="flex flex-col gap-4">
											<FormField
												control={createForm.control}
												name="max_debt"
												render={({ field }) => (
													<FormItem>
														<FormLabel>
															{t("relations.edit.inputMaxDebt")}
														</FormLabel>
														<FormControl>
															<Input
																type="number"
																placeholder={t("relations.edit.inputMaxDebt")}
																value={field.value ?? ""}
																onChange={field.onChange}
															/>
														</FormControl>
														<FormMessage />
													</FormItem>
												)}
											/>

											<div>
												<Controller
													name="use_inherited_price"
													control={createForm.control}
													render={({ field }) => (
														<Checkbox
															label={t(
																"relations.edit.checkboxInheritGlobalPrice",
															)}
															checked={field.value}
															onCheckedChange={(checked) => {
																const next = checked === true;
																field.onChange(next);
																if (next) {
																	createForm.setValue("local_price", null);
																	createForm.clearErrors("local_price");
																}
															}}
														/>
													)}
												/>
												<FormDescription>
													{t("relations.edit.inheritGlobalPriceHelper")}
												</FormDescription>

												{!useInheritedPrice && (
													<FormField
														control={createForm.control}
														name="local_price"
														render={({ field }) => (
															<FormItem className="mt-4">
																<FormLabel required>
																	{t("relations.edit.inputLocalPrice")}
																</FormLabel>
																<FormControl>
																	<Input
																		type="number"
																		placeholder={t(
																			"relations.edit.inputLocalPrice",
																		)}
																		value={field.value ?? ""}
																		onChange={field.onChange}
																	/>
																</FormControl>
																<FormMessage />
															</FormItem>
														)}
													/>
												)}
											</div>

											<Controller
												name="is_allowed_to_order"
												control={createForm.control}
												render={({ field }) => (
													<FormItem>
														<Checkbox
															label={t("relations.edit.checkboxAllowToOrder")}
															checked={field.value}
															onCheckedChange={field.onChange}
														/>
													</FormItem>
												)}
											/>
										</div>
									)}
								</form>
							</Form>
						)}
					</DialogScrollableArea>
					<DialogFooter
						buttons={
							createdCredentials
								? {
										primary: {
											label: t("admin.users.done"),
											props: {
												type: "button",
												onClick: handleCloseCreate,
											},
										},
									}
								: {
										secondary: {
											label: t("button.cancel"),
											props: {
												type: "button",
												disabled: createUserMutation.isPending,
												onClick: handleCloseCreate,
											},
										},
										primary: {
											label: t("button.submit"),
											props: {
												type: "submit",
												form: "admin-user-create-form",
												loading: createUserMutation.isPending,
												disabled: createUserMutation.isPending,
											},
										},
									}
						}
					/>
				</DialogContent>
			</Dialog>

			<STAdminUsersFilterForm
				onSearch={handleSearch}
				defaultValues={filters}
				facetCounts={facetCounts}
			/>

			<DataTable
				columns={columns}
				data={rows}
				totalItems={rowCount}
				server={server}
				isLoading={isLoading}
				isFetching={isFetching}
				error={error ? getErrorInterface(error) : null}
				onRetry={refetch}
				getRowId={(row) => row.id}
			/>

			<EditUserDialog
				open={Boolean(editingUser)}
				user={editingUser}
				loading={updateUserMutation.isPending}
				onClose={() => setEditingUser(null)}
				onSubmit={handleEditSubmit}
			/>

			<ResetPasswordDialog
				open={Boolean(resettingUser)}
				user={resettingUser}
				onClose={() => setResettingUser(null)}
			/>
		</div>
	);
};

export default STAdminUsers;
